import { AHText, Box, TouchableBox, useScaleFactor } from "arkh";
import React, { useCallback } from "react";
import { FlatList, Platform, View } from "react-native";
import { graphql, useFragment } from "react-relay";
import { WidgetOverviewSize } from "../globalTypes";
import { Image } from "expo-image";
import { useSetWidgetFocus } from "../useSetFocus";
import { formatNumberValue } from "../WidgetTaskSingle/NumberTask/formatNumber";

import { WidgetIntegrationPushAppy_widgetIntegrationPushAppy$key } from "./__generated__/WidgetIntegrationPushAppy_widgetIntegrationPushAppy.graphql";

export function WidgetIntegrationPushAppy(props: {
  widget: WidgetIntegrationPushAppy_widgetIntegrationPushAppy$key;
  storecastId?: string;
  mode?: "FOCUS" | "IDLE";
  interactive?: boolean;
}) {
  const widget = useFragment(
    graphql`
      fragment WidgetIntegrationPushAppy_widgetIntegrationPushAppy on WidgetIntegrationPushAppy {
        id
        title
        overviewSize
        leaderboard {
          id
          title
          goal
          progress
          total
        }
      }
    `,
    props.widget
  );
  const sf = useScaleFactor();

  const setFocus = useSetWidgetFocus(widget.id, props.storecastId);

  const renderItem = useCallback(
    ({
      item,
      index,
    }: {
      item: (typeof widget.leaderboard)[number] | null;
      index: number;
    }) => {
      if (!item) {
        return null;
      }
      return (
        <Box
          key={item.id}
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            paddingVertical:
              widget.overviewSize === WidgetOverviewSize.TWO_X_ONE
                ? sf * 5.5
                : sf * 3.3,
          }}
          spaceHorizontal="medium"
          spaceRight="large"
        >
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <AHText
              style={{ minWidth: sf * 30 }}
              textAlign="center"
              type="title-small"
            >
              {index + 1}.{" "}
            </AHText>
            <AHText type="title-small">{item?.title}</AHText>
          </View>
          <View style={{ flexBasis: "30%", justifyContent: "flex-end" }}>
            {item.progress === null ? (
              <>
                {item.total !== null ? (
                  <AHText
                    spaceVertical="xsmall"
                    textAlign="right"
                    type="title-small"
                  >
                    {formatNumberValue("CUSTOM", item.total)}
                  </AHText>
                ) : null}
              </>
            ) : (
              <>
                <AHText textAlign="right" type="title-small">
                  {item.progress === null
                    ? ""
                    : Math.round(item.progress * 100) + "%"}
                </AHText>
                {item.total !== null && item.goal !== null ? (
                  <AHText textAlign="right">
                    {formatNumberValue("CUSTOM", item.total)} av{" "}
                    {formatNumberValue("CUSTOM", item.goal)}
                  </AHText>
                ) : null}
              </>
            )}
          </View>
        </Box>
      );
    },
    [widget, sf]
  );

  return (
    <TouchableBox
      disabled={props.mode !== "FOCUS"}
      style={{ flex: 1 }}
      color="surface"
      onPress={props.interactive === false ? undefined : () => setFocus()}
    >
      <Box
        spaceTop="small"
        spaceLeft="large"
        style={{
          flexDirection: "column",
          alignItems: "flex-start",
          alignContent: "flex-start",
          justifyContent: "flex-start",
        }}
      >
        <Image
          style={{
            width: Math.floor(100 * sf),
            height: Math.floor(23 * sf),
          }}
          contentFit="contain"
          source={require("./logo.png")}
        />
      </Box>

      {props.interactive === false ? (
        <View>
          {widget.leaderboard.map((item, index) => renderItem({ item, index }))}
        </View>
      ) : (
        <FlatList
          data={widget.leaderboard ?? []}
          showsVerticalScrollIndicator={Platform.OS !== "web"}
          renderItem={renderItem}
        />
      )}
    </TouchableBox>
  );
}
